// react/utils
import { useEffect, useRef, useState } from 'react';
import { useLatestRef } from '../../hooks/useLatestRef';

// types
import type { Period, PeriodChangeReason } from '../PeriodInput.types';

type UsePeriodInputSyncReportingArgs = {
  // What a sync writes. The settle timer re-schedules on these, so it waits for the values to
  // land instead of for the setters alone.
  startDate: Date | undefined;
  startTime: Date | undefined;
  durationHours: string;
  durationMinutes: string;
  // Minute-granular timestamps from `usePeriodInputState` -- see there for why numbers.
  propsStartMs: number;
  propsEndMs: number;
  fullStartMs: number | null;
  fullEndMs: number | null;
  fullEndMinuteMs: number | null;
  syncFromProps: (startMs: number, endMs: number) => void;
  onChange: (period: Period, reason: PeriodChangeReason) => void;
};

/**
 * The effect choreography: pulling an incoming period into the fields, waiting for that to
 * settle, and reporting what the component ended up showing when it differs from what it was
 * handed. Kept apart from the state and the UI because the ordering between the three effects
 * is the whole point, and the flags below only make sense read together.
 *
 * Returns `markUserEdited`, which the input handlers call so a report can say whether the user
 * typed the value or the component derived it.
 */
export const usePeriodInputSyncReporting = ({
  startDate,
  startTime,
  durationHours,
  durationMinutes,
  propsStartMs,
  propsEndMs,
  fullStartMs,
  fullEndMs,
  fullEndMinuteMs,
  syncFromProps,
  onChange
}: UsePeriodInputSyncReportingArgs) => {
  'use memo';

  // Make sure onChange updates only happen as a result
  // of manual changes, not the initial load
  const syncingFromProps = useRef(false);

  // Bumped when the flag above clears, and only then. The reporting effect depends on it so
  // that a correction it had to suppress mid-sync -- an incoming period this component clamps,
  // for instance -- gets reported once the sync settles. Without it that correction is lost:
  // the flag clears from a `setTimeout`, which triggers no render, and the effect's other
  // dependencies are timestamps that did not move.
  const [syncSettledCount, setSyncSettledCount] = useState(0);

  // Set by the reporting effect when it had a change to report but was mid-sync. It keeps the
  // bump above to the syncs that owe the parent something: a sync that ends up agreeing with
  // the props it came from -- the common case, once the parent applied what was reported -- has
  // nothing to report, and re-rendering this component and its three pickers for it is waste.
  const suppressedReport = useRef(false);

  // Set by the input handlers, cleared whenever a new period comes in from props. It lets the
  // reporting effect tell apart the two kinds of change it reports: one the user made, and one
  // this component derived on its own -- a clamped period, for instance.
  const userEdited = useRef(false);

  // Both are handed over fresh every render -- `onChange` because callers pass it inline,
  // `syncFromProps` because it closes over this render's setters. Listing either as a
  // dependency would put that identity churn straight into the effects below.
  const latestOnChange = useLatestRef(onChange);
  const latestSyncFromProps = useLatestRef(syncFromProps);

  useEffect(() => {
    syncingFromProps.current = true;
    userEdited.current = false;

    latestSyncFromProps.current(propsStartMs, propsEndMs);
  }, [propsStartMs, propsEndMs, latestSyncFromProps]);

  useEffect(() => {
    if (!syncingFromProps.current) return;

    // Re-scheduled on every state change a sync causes, so the cleanup matters: without it a
    // single sync leaves several timers pending, each bumping the count, and each bump lets the
    // reporting effect fire again. It also keeps a running sync from being settled early by a
    // timer of the previous one, and stops the timer from firing after unmount.
    const settleTimer = setTimeout(() => {
      syncingFromProps.current = false;

      if (suppressedReport.current) {
        setSyncSettledCount((count) => count + 1);
      }
    });

    return () => clearTimeout(settleTimer);
  }, [startDate, startTime, durationHours, durationMinutes]);

  useEffect(() => {
    if (fullStartMs === null || fullEndMs === null || fullEndMinuteMs === null)
      return;

    const startChanged = propsStartMs !== fullStartMs;
    const endChanged = propsEndMs !== fullEndMinuteMs;

    // Weighed before the sync check below, so a sync that agrees with its props is recorded as
    // owing nothing -- the settle timer reads this to decide whether a render is needed at all.
    if (!startChanged && !endChanged) {
      suppressedReport.current = false;
      return;
    }

    if (syncingFromProps.current) {
      suppressedReport.current = true;
      return;
    }

    suppressedReport.current = false;

    latestOnChange.current(
      {
        start: new Date(fullStartMs),
        end: new Date(fullEndMs)
      },
      userEdited.current ? 'user-edit' : 'correction'
    );
  }, [
    fullStartMs,
    fullEndMs,
    fullEndMinuteMs,
    propsStartMs,
    propsEndMs,
    syncSettledCount,
    latestOnChange
  ]);

  const markUserEdited = () => {
    userEdited.current = true;
  };

  return { markUserEdited };
};
